/**
 * Photo management for profile settings page
 */
import { mediaApi, ProfilePhoto } from '../../api';
import { showToast, showApiError } from '../../main';
import { MAX_PHOTOS, MAX_FILE_SIZE_BYTES } from '../../config';
import { openCropEditor } from './crop-editor';
import { updateProfileBadgeFromDOM } from './auto-save';
import socket from '../../socket';
import { renderUploadButton, setupUploadButton } from '../../components/upload-button';
import {
  TOAST_PHOTO_DELETED,
  TOAST_PRIMARY_PHOTO_UPDATED,
  TOAST_CROP_SAVED,
  TOAST_FAILED_UPLOAD_PHOTOS,
  toastMaxPhotos,
  toastUploadLimited,
  toastFileTooLarge,
  toastPhotosUploaded,
  toastPartialUpload,
  TOAST_PHOTO_NOT_ACCEPTED,
  API_ERR_DELETE_PHOTO,
  API_ERR_SET_PRIMARY,
  API_ERR_REORDER,
} from '../../utils/app-strings';

// Photo state
let profilePhotos: ProfilePhoto[] = [];
let moderationListenerSetup = false;

export function getPhotos(): ProfilePhoto[] {
  return profilePhotos;
}

export async function loadPhotos(): Promise<void> {
  const grid = document.getElementById('photo-grid');

  // Listen for moderation rejections (once)
  if (!moderationListenerSetup) {
    moderationListenerSetup = true;
    socket.on('media:moderation_failed', (_data: { mediaId: string }) => {
      showToast(TOAST_PHOTO_NOT_ACCEPTED, 'error');
      // Refresh photos to reflect the removal
      mediaApi.getPhotos().then(res => {
        profilePhotos = res.data;
        renderPhotoGrid();
        renderAboutPhotoStrip();
      }).catch(() => {});
    });
    socket.on('media:moderation_approved', (_data: { mediaId: string }) => {
      // Refresh photos to show the approved thumbnail
      mediaApi.getPhotos().then(res => {
        profilePhotos = res.data;
        renderPhotoGrid();
        renderAboutPhotoStrip();
      }).catch(() => {});
    });
  }

  try {
    const response = await mediaApi.getPhotos();
    profilePhotos = response.data;
    renderPhotoGrid();
  } catch (err) {
    // Show add button in grid
    if (grid) {
      grid.innerHTML = renderUploadButton('add-photo-btn');
      setupAddPhotoButton();
      renderAboutPhotoStrip();
    }
  }
}

export function renderAboutPhotoStrip(): void {
  const strip = document.getElementById('about-photos-strip');
  if (!strip) return;

  const uploadTile = profilePhotos.length < MAX_PHOTOS
    ? `<div class="about-photo-tile about-photo-upload" id="about-photo-upload-btn">
         <i class="fas fa-plus"></i>
       </div>`
    : '';

  const photoTiles = profilePhotos.map(photo => {
    if (photo.moderation_status !== 'approved') {
      return `<div class="about-photo-tile about-photo-pending" data-photo-nav>
        <div class="spinner" style="width: 16px; height: 16px;"></div>
      </div>`;
    }
    const imgSrc = photo.files.square_medium?.url || photo.files.medium?.url || photo.files.thumb?.url;
    return `<div class="about-photo-tile about-photo-existing" data-photo-nav>
      <img src="${imgSrc}" alt="Photo" />
    </div>`;
  }).join('');

  strip.innerHTML = uploadTile + photoTiles;

  // Upload tile: click + drag-and-drop via shared component
  setupUploadButton('about-photo-upload-btn', handleFileUpload);

  // Clicking a photo tile navigates to the My Photos tab
  strip.querySelectorAll('[data-photo-nav]').forEach(tile => {
    tile.addEventListener('click', () => {
      // Deactivate all tabs and content, then activate photos
      document.querySelectorAll('.seg-btn').forEach(t => t.classList.remove('active'));
      document.querySelectorAll('.seg-panel').forEach(c => c.classList.remove('active'));
      document.getElementById('tab-photos')?.classList.add('active');
      history.replaceState(null, '', '#/my-photos');
    });
  });
}

function buildPhotoMetaDetail(photo: ProfilePhoto): string {
  const rows: string[] = [];

  if (photo.vision_json?.attractiveness_score != null) {
    const score = photo.vision_json.attractiveness_score;
    rows.push(`<div class="photo-meta-row"><span class="photo-meta-label">Score</span><span class="photo-meta-value"><span class="photo-meta-score-bar"><span class="photo-meta-score-fill" style="width: ${score}%"></span></span> ${score}/100</span></div>`);
  }

  if (photo.vision_json?.people_count != null) {
    rows.push(`<div class="photo-meta-row"><span class="photo-meta-label">People</span><span class="photo-meta-value">${photo.vision_json.people_count}</span></div>`);
  }

  if (photo.caption_user) {
    rows.push(`<div class="photo-meta-row"><span class="photo-meta-label">My Caption</span><span class="photo-meta-value">${photo.caption_user}</span></div>`);
  }

  if (photo.captured_at) {
    const date = new Date(photo.captured_at).toLocaleDateString();
    rows.push(`<div class="photo-meta-row"><span class="photo-meta-label">Taken</span><span class="photo-meta-value">${date}</span></div>`);
  }

  if (photo.vision_json?.scene_description) {
    rows.push(`<p class="photo-meta-scene">${photo.vision_json.scene_description}</p>`);
  }

  return rows.join('');
}

export function renderPhotoGrid(): void {
  const grid = document.getElementById('photo-grid');
  if (!grid) return;

  const photosHtml = profilePhotos.map((photo, index) => {
    const isPrimary = index === 0;
    const isLast = index === profilePhotos.length - 1;
    const photoNumber = index + 1; // 1-based numbering for display
    const numberLabel = isPrimary ? `${photoNumber} / Primary` : `${photoNumber}`;
    // Use actual sort_order from database, not array index
    const sortOrder = photo.sort_order;
    // Show "reviewing" only for recent uploads - if moderation hasn't completed
    // after 5 minutes, treat as approved (server crash / edge case safety net)
    const ageMs = Date.now() - new Date(photo.created_at).getTime();
    const pending = photo.moderation_status !== 'approved' && ageMs < 5 * 60 * 1000;

    if (pending) {
      return `
        <div class="photo-item-wrapper">
          <div class="photo-item photo-item-pending" data-media-id="${photo.id}" data-sort-order="${sortOrder}">
            <div class="photo-pending-placeholder">
              <div class="spinner"></div>
              <span>Reviewing...</span>
            </div>
            <div class="photo-item-actions">
              <button class="btn-round photo-action-btn photo-delete-btn" data-media-id="${photo.id}" title="Delete">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      `;
    }

    const imgSrc = photo.files.square_medium?.url || photo.files.medium?.url || photo.files.thumb?.url;

    return `
      <div class="photo-item-wrapper">
        <div class="photo-item" data-media-id="${photo.id}" data-sort-order="${sortOrder}">
          <img src="${imgSrc}" alt="Profile photo" />
          <div class="photo-number-badge">${numberLabel}</div>
          <div class="photo-item-actions">
            ${!isPrimary ? `
              <button class="btn-round photo-action-btn photo-up-btn" data-media-id="${photo.id}" data-sort-order="${sortOrder}" title="Move up">
                <i class="fas fa-arrow-up"></i>
              </button>
              <button class="btn-round photo-action-btn photo-first-btn" data-media-id="${photo.id}" title="Make first">
                <i class="fas fa-angles-up"></i>
              </button>
            ` : ''}
            ${!isLast ? `
              <button class="btn-round photo-action-btn photo-last-btn" data-media-id="${photo.id}" title="Make last">
                <i class="fas fa-angles-down"></i>
              </button>
              <button class="btn-round photo-action-btn photo-down-btn" data-media-id="${photo.id}" data-sort-order="${sortOrder}" title="Move down">
                <i class="fas fa-arrow-down"></i>
              </button>
            ` : ''}
            <button class="btn-round photo-action-btn photo-crop-btn" data-media-id="${photo.id}" title="Crop">
              <i class="fas fa-crop-alt"></i>
            </button>
            <button class="btn-round photo-action-btn photo-delete-btn" data-media-id="${photo.id}" title="Delete">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
        ${(() => {
          const detail = buildPhotoMetaDetail(photo);
          if (detail) {
            return `<div class="photo-meta-toggle" data-meta-id="${photo.id}">
              <span class="photo-meta-caption">${photo.caption_ai || 'Details'}</span>
              <span class="photo-meta-chevron"><i class="fas fa-chevron-down"></i></span>
            </div>
            <div class="photo-meta-detail" id="photo-meta-${photo.id}">${detail}</div>`;
          }
          if (photo.caption_ai) {
            return `<div class="photo-ai-caption">${photo.caption_ai}</div>`;
          }
          return '';
        })()}
      </div>
    `;
  }).join('');

  const addButtonHtml = profilePhotos.length < MAX_PHOTOS
    ? renderUploadButton('add-photo-btn')
    : '';

  grid.innerHTML = addButtonHtml + photosHtml;

  setupAddPhotoButton();
  setupPhotoActions();
  updateProfileBadgeFromDOM();
  renderAboutPhotoStrip();
}

export function setupAddPhotoButton(): void {
  setupUploadButton('add-photo-btn', handleFileUpload);
}

async function handleFileUpload(files: File[]): Promise<void> {
  // Calculate how many photos we can still add
  const slotsAvailable = MAX_PHOTOS - profilePhotos.length;
  const filesToUpload = files.slice(0, slotsAvailable);

  if (filesToUpload.length === 0) {
    showToast(toastMaxPhotos(MAX_PHOTOS), 'error');
    return;
  }

  if (files.length > slotsAvailable) {
    showToast(toastUploadLimited(slotsAvailable, MAX_PHOTOS), 'error');
  }

  // Validate all file sizes
  const oversizedFiles = filesToUpload.filter(f => f.size > MAX_FILE_SIZE_BYTES);
  if (oversizedFiles.length > 0) {
    showToast(toastFileTooLarge(oversizedFiles.length), 'error');
  }
  const validFiles = filesToUpload.filter(f => f.size <= MAX_FILE_SIZE_BYTES);

  if (validFiles.length === 0) {
    return;
  }

  // Show uploading state
  const addBtn = document.getElementById('add-photo-btn');
  if (addBtn) {
    addBtn.innerHTML = '<div class="spinner" style="width: 24px; height: 24px;"></div>';
    addBtn.classList.add('uploading');
  }

  let successCount = 0;
  let failCount = 0;

  // Upload files sequentially to maintain order
  for (const file of validFiles) {
    try {
      const response = await mediaApi.upload(file);
      profilePhotos.push(response.data);
      successCount++;
    } catch (err: any) {
      failCount++;
      console.error('Failed to upload:', file.name, err);
    }
  }

  renderPhotoGrid();

  if (successCount > 0 && failCount === 0) {
    showToast(toastPhotosUploaded(successCount), 'success');
  } else if (successCount > 0 && failCount > 0) {
    showToast(toastPartialUpload(successCount, failCount), 'error');
  } else {
    showToast(TOAST_FAILED_UPLOAD_PHOTOS, 'error');
  }
}


function setupPhotoActions(): void {
  // Delete buttons
  document.querySelectorAll('.photo-delete-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const mediaId = (btn as HTMLElement).dataset.mediaId;
      if (!mediaId) return;

      try {
        const response = await mediaApi.delete(mediaId);
        profilePhotos = response.data;
        renderPhotoGrid();
        showToast(TOAST_PHOTO_DELETED, 'success');
      } catch (err: any) {
        showApiError(err, API_ERR_DELETE_PHOTO);
      }
    });
  });

  // Make primary (move to position 0) buttons
  document.querySelectorAll('.photo-first-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const mediaId = (btn as HTMLElement).dataset.mediaId;
      if (!mediaId) return;

      try {
        const response = await mediaApi.reorder(mediaId, 0);
        profilePhotos = response.data;
        renderPhotoGrid();
        showToast(TOAST_PRIMARY_PHOTO_UPDATED, 'success');
      } catch (err: any) {
        showApiError(err, API_ERR_SET_PRIMARY);
      }
    });
  });

  // Move up buttons (decrease sort order)
  document.querySelectorAll('.photo-up-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const mediaId = (btn as HTMLElement).dataset.mediaId;
      const sortOrder = parseInt((btn as HTMLElement).dataset.sortOrder || '0', 10);
      if (!mediaId || sortOrder <= 0) return;

      try {
        const response = await mediaApi.reorder(mediaId, sortOrder - 1);
        profilePhotos = response.data;
        renderPhotoGrid();
      } catch (err: any) {
        showApiError(err, API_ERR_REORDER);
      }
    });
  });

  // Crop buttons
  document.querySelectorAll('.photo-crop-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const mediaId = (btn as HTMLElement).dataset.mediaId;
      if (!mediaId) return;

      const photo = profilePhotos.find(p => p.id === mediaId);
      if (!photo) return;

      openCropEditor(photo, async (crop) => {
        const response = await mediaApi.updateCrop(mediaId, crop);
        profilePhotos = response.data;
        renderPhotoGrid();
        showToast(TOAST_CROP_SAVED, 'success');
      });
    });
  });

  // Move down buttons (increase sort order)
  document.querySelectorAll('.photo-down-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const mediaId = (btn as HTMLElement).dataset.mediaId;
      const sortOrder = parseInt((btn as HTMLElement).dataset.sortOrder || '0', 10);
      if (!mediaId) return;

      try {
        const response = await mediaApi.reorder(mediaId, sortOrder + 1);
        profilePhotos = response.data;
        renderPhotoGrid();
      } catch (err: any) {
        showApiError(err, API_ERR_REORDER);
      }
    });
  });

  // Make last buttons (move to end)
  document.querySelectorAll('.photo-last-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const mediaId = (btn as HTMLElement).dataset.mediaId;
      if (!mediaId) return;

      try {
        const response = await mediaApi.reorder(mediaId, profilePhotos.length - 1);
        profilePhotos = response.data;
        renderPhotoGrid();
      } catch (err: any) {
        showApiError(err, API_ERR_REORDER);
      }
    });
  });

  // Photo meta expand/collapse toggles
  document.querySelectorAll('.photo-meta-toggle').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const metaId = (toggle as HTMLElement).dataset.metaId;
      if (!metaId) return;
      const detail = document.getElementById(`photo-meta-${metaId}`);
      if (!detail) return;
      toggle.classList.toggle('expanded');
      detail.classList.toggle('expanded');
    });
  });
}
